import {KennethMustDie} from "../KennethMustDie";
import {SPECIAL_SYMBOL_NAMES} from "../server/IKMDData";
import {KMDSymbol} from "../reels/KMDSymbol";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {KMDSound} from "../sound/KMDSound";

export class KMDStickyWilds extends PIXI.Container {

    private _wilds: KMDSymbol[][];
    private _locks: PIXI.spine.Spine[][];
    private _lockYOffset100Size: number[] = [25, 75, 125];

    constructor() {
        super();
        this.name = "KMDStickyWilds";

        this.createItems();
    }

    public showStickyWild(reelId: number, symId: number, symIdInStopSymbols: number): void {
        // #417
        if (symId < 2 && (KennethMustDie.stickyXNudges.checkIfSticky(reelId, 1) || KennethMustDie.stickyXNudges.checkIfSticky(reelId, 2))) {
            this.unlockWild(reelId, symId);
            return;
        }

        const wild: KMDSymbol = this._wilds[reelId][symId],
            multi: number = KennethMustDie.model.data.multiplier[reelId][symId] || 1;

        wild.visible = true;
        wild.setSplitState(multi);

        this.lockWild(reelId, symId);

        const randomLowName: string = ArrayHelper.getRandomValue(KennethMustDie.LOW_SYMBOL_NAMES),
            spinOutSym: KMDSymbol = (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(symIdInStopSymbols).positionable as KMDSymbol);
        spinOutSym.updateTexture(randomLowName, 1);
    }

    public lockWild(reelId: number, symId: number): void {
        // no lock on wild during normal or normal_avalanche mode
        if (KennethMustDie.model.data.mode.indexOf("NORMAL") > -1) {
            return;
        }

        // #417
        if (symId < 2 && (KennethMustDie.stickyXNudges.checkIfSticky(reelId, 1) || KennethMustDie.stickyXNudges.checkIfSticky(reelId, 2))) {
            this.unlockWild(reelId, symId);
            return;
        }

        const lock: PIXI.spine.Spine = this._locks[reelId][symId];

        if (!lock) {
            return;
        }

        lock.name = "locked_150";
        if (lock.visible) {
            lock.state.setAnimation(0, lock.name, true);
            lock.y = (symId * KennethMustDie.SYMBOL_HEIGHT);

            return;
        }

        lock.visible = true;
        lock.updateTransform();
        KennethMustDie.createTimelineForAnimation(lock, 0, (lock.name + "_start"), false).eventCallback("onComplete", () => {
            lock.autoUpdate = true;

            lock.state.setAnimation(0, lock.name, true);
        });
    }

    public changeToReelSymbol(reelId: number, isAvalanche: boolean): void {
        // no sticky wild during normal or normal_avalanche mode
        if (KennethMustDie.model.data.mode.indexOf("NORMAL") > -1) {
            return;
        }

        this._wilds[reelId].forEach((wild: KMDSymbol, symId: number) => {
            if (wild.visible) {
                const multi: number = KennethMustDie.model.data.multiplier[reelId][symId],
                    symbol: KMDSymbol = (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(symId + (isAvalanche ? 0 : 1)).positionable as KMDSymbol);

                symbol.originalName = SPECIAL_SYMBOL_NAMES.W;
                symbol.updateTexture(SPECIAL_SYMBOL_NAMES.W, multi);

                this.hideStickyWild(reelId, symId);
            }
        });
    }

    public changeLockSize(reelId: number, symId: number, newSize: number): void {
        const lock: PIXI.spine.Spine = this._locks[reelId][symId];
        lock.name = "locked_" + newSize;
        lock.state.setAnimation(0, lock.name, true);
        lock.y = (symId * KennethMustDie.SYMBOL_HEIGHT) - this._lockYOffset100Size[symId];
    }

    public hideAllStickyWilds(): void {
        this._wilds.forEach((items: KMDSymbol[], reelId: number) => {
            items.forEach((item: KMDSymbol, symId: number) => {
                this.hideStickyWild(reelId, symId);

                this.unlockWild(reelId, symId);
            });
        });
    }

    public checkIfSticky(reelId: number, symId: number): boolean {
        return (this._locks[reelId] && this._locks[reelId].length > 0 && this._locks[reelId][symId].visible);
    }

    public unlockWild(reelId: number, symId: number): void {
        const lock: PIXI.spine.Spine = this._locks[reelId][symId];

        if (!lock || !lock.visible) {
            return;
        }
        lock.state.clearTracks();

        // #480
        KennethMustDie.soundPlayer.playSound(KMDSound.CHAINED_UNLOCKED);

        KennethMustDie.createTimelineForAnimation(lock, 0, (lock.name + "_disappear"), false).eventCallback("onComplete", () => {
            lock.visible = false;

            lock.skeleton.setToSetupPose();
            lock.y = (symId * KennethMustDie.SYMBOL_HEIGHT);
        });
    }

    private hideStickyWild(reelId: number, symId: number): void {
        this._wilds[reelId][symId].visible = false;
        this._wilds[reelId][symId].splitParts = 1;
    }

    private createItems(): void {
        const totalRows: number = 3;
        this.y = KennethMustDie.MAIN_REELS_TOP;

        this._wilds = [];
        this._wilds.push([]);

        this._locks = [];
        this._locks.push([]);

        // only in middle 3 main reels
        for (let reelId: number = 1; reelId < KennethMustDie.REELS_NUM - 1; reelId++) {
            this._wilds.push([]);
            this._locks.push([]);

            for (let symId: number = 0; symId < totalRows; symId++) {
                const wild: KMDSymbol = new KMDSymbol(SPECIAL_SYMBOL_NAMES.W),
                    lock: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("locks"));

                wild.visible = lock.visible = false;
                wild.position.set(((reelId * KennethMustDie.SYMBOL_WIDTH) + KennethMustDie.SYMBOL_WIDTH / 2), (symId * KennethMustDie.SYMBOL_HEIGHT));

                lock.position = wild.position;
                this._wilds[reelId].push(wild);
                this._locks[reelId].push(lock);

                this.addChild(wild, lock);
            }
        }

        this._wilds.push([]);
        this._locks.push([]);
    }
}